
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where,
  orderBy,
  writeBatch,
  serverTimestamp,
  Timestamp,
  deleteField,
  arrayUnion,
  arrayRemove,
  DocumentSnapshot,
  DocumentData,
} from "firebase/firestore";
import { ref as storageRef, listAll, getDownloadURL, getBlob, uploadBytes, deleteObject } from "firebase/storage";
import { firestore, storage } from './firebase';
import type { Customer, CustomerStatus, Appointment, JobType, CallLog, CustomerGroup, Note, CallerName, NoteAuthor, Carpenter, FullCustomerRecord, Photo } from './types';
import { zipCodeCenters } from "./zip-data";


const toIso = (v: any): string | undefined =>
  v instanceof Timestamp ? v.toDate().toISOString() : v ?? undefined;

const mapStatus = (s: any): CustomerStatus => {
  const known: CustomerStatus[] = [
    "pending", "call-to-schedule-carpentry", "call-to-schedule-paint", "scheduled-carpentry", "scheduled-paint", "scheduled-gas", "reschedule", "call-back", "not-responsive", "wrong-contact-info", "waiting-on-callback", "waiting-on-cms", "submit", "complete"
  ];
  if (known.includes(s)) return s;
  if (s === "Active" || s === "Lead" || s === "Inactive") return "pending";
  return "pending";
};

const defaultNYC = { lat: 40.7128, lon: -74.0060 };

export function estimateCoordinates(address: string) {
  const zipMatch = address.match(/\b(\d{5})\b/);
  if (zipMatch && zipCodeCenters[zipMatch[1]]) {
    const center = zipCodeCenters[zipMatch[1]];
    return {
        lat: center.lat,
        lon: center.lon,
    }
  }
  return defaultNYC;
}

export async function geocodeAddress(address: string): Promise<{ lat: number; lon: number; precise: boolean }> {
  const apiKey = process.env.NEXT_PUBLIC_TOMTOM_API_KEY;
  if (!apiKey) return { ...estimateCoordinates(address), precise: false };
  try {
    // Biased to the service area for the same reason lookupPostalCode is: an
    // unbiased "1055 SENECA ST, Queens, NY" resolves to Buffalo.
    const res = await fetch(
      `https://api.tomtom.com/search/2/geocode/${encodeURIComponent(address)}.json` +
        `?key=${apiKey}&countrySet=US&limit=1` +
        `&lat=${NYC_BIAS.lat}&lon=${NYC_BIAS.lon}&radius=${NYC_BIAS.radius}`
    );
    if (!res.ok) return { ...estimateCoordinates(address), precise: false };
    const data = await res.json();
    const result = data.results?.[0];
    if (result?.position) {
      return { lat: result.position.lat, lon: result.position.lon, precise: true };
    }
  } catch {
    // fall through
  }
  return { ...estimateCoordinates(address), precise: false };
}

/**
 * Roughly the middle of the five boroughs, with a radius that reaches
 * Tottenville and eastern Queens.
 *
 * Every job this app schedules is in New York City, and without this bias
 * TomTom answers nationally: "1055 SENECA ST, Queens, NY" resolves to 1055
 * Seneca Street in BUFFALO, ZIP 14210. Biased, the same query returns 1055
 * Seneca Avenue, Ridgewood, 11385 — the right building. 45 km is wide enough
 * to cover the service area and far too small to reach upstate.
 */
const NYC_BIAS = { lat: 40.7128, lon: -73.95, radius: 45000 };

/**
 * The ZIP code TomTom holds for an address, or null.
 *
 * The intake spreadsheet carries a street and a borough but never a ZIP, and
 * the New Customer form will not save without one. Rather than make somebody
 * look up five digits they already implied, the same geocode call that gives
 * us coordinates is asked for the postal code as well.
 *
 * Returns null rather than a guess whenever it cannot be sure — no key, a
 * failed request, no match, or a result whose postal code is not five digits.
 * A blank ZIP the user fills in is recoverable; a wrong one that rides along
 * into an export is not. Callers are expected to check the answer against a
 * known list of service-area ZIPs as a second line of defence.
 */
export async function lookupPostalCode(address: string): Promise<string | null> {
  const apiKey = process.env.NEXT_PUBLIC_TOMTOM_API_KEY;
  if (!apiKey) return null;
  try {
    const res = await fetch(
      `https://api.tomtom.com/search/2/geocode/${encodeURIComponent(address)}.json` +
        `?key=${apiKey}&countrySet=US&limit=1` +
        `&lat=${NYC_BIAS.lat}&lon=${NYC_BIAS.lon}&radius=${NYC_BIAS.radius}`
    );
    if (!res.ok) return null;
    const data = await res.json();
    // TomTom gives a space-separated list when a match straddles more than one
    // ZIP; the first is the one for the point it actually matched.
    const zip = String(data.results?.[0]?.address?.postalCode ?? '').trim().split(/[\s,]+/)[0];
    return /^\d{5}$/.test(zip) ? zip : null;
  } catch {
    return null;
  }
}

// Maps link for a customer's address. Prefers a precisely-geocoded coordinate
// (bypasses Google's own text-search resolution, which can snap an oddly
// numbered/hyphenated address to the nearest address it has indexed instead
// of the exact one) and falls back to a text search when no precise geocode
// is on file.
export function googleMapsAddressUrl(customer: Pick<Customer, 'address' | 'location' | 'locationPrecise' | 'locationAddress'>): string {
  if (customer.location && customer.locationPrecise && sameAddress(customer.locationAddress, customer.address)) {
    return `https://www.google.com/maps/search/?api=1&query=${customer.location.lat},${customer.location.lon}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customer.address)}`;
}

// Coords are only trustworthy when we know which address produced them.
// Records with no provenance stamp (written before locationAddress existed, or
// by an import) fall back to a text search on the current address, so an edited
// address can never resolve to the previous location.
const normalizeAddress = (a: string) => a.trim().replace(/\s+/g, ' ').replace(/[.,]/g, '').toLowerCase();
export function sameAddress(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  return normalizeAddress(a) === normalizeAddress(b);
}

export const mapLocation = (raw: any): { lat: number; lon: number } | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const lat = Number(raw.lat ?? raw.latitude);
  const lon = Number(raw.lon ?? raw.lng ?? raw.longitude);
  return Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : undefined;
};


// --- Collection References ---
// We wrap these in a function to ensure firestore is initialized
const getCustomersCol = () => collection(firestore, "customers");
const getGroupsCol = () => collection(firestore, "groups");
const getAppointmentsCol = () => collection(firestore, "appointments");
const getCallerNamesCol = () => collection(firestore, "callerNames");
const getNoteAuthorsCol = () => collection(firestore, "noteAuthors");
const getCarpentersCol = () => collection(firestore, "carpenters");


// --- CUSTOMERS ---
function mapCustomerFromDoc(d: DocumentSnapshot<DocumentData>): Customer {
    const x = d.data() as any;
    let location = mapLocation(x.location);
    let locationPrecise = !!x.locationPrecise;
    if (!location && x.address) {
        location = estimateCoordinates(x.address);
        locationPrecise = false;
    }

    return {
      id: d.id,
      name: x.name ?? "",
      phone: x.phone ?? "",
      address: x.address ?? "",
      status: mapStatus(x.status),
      groupId: x.groupId ?? undefined,
      lastUpdated: toIso(x.lastUpdated ?? x.updatedAt ?? x.createdAt)!,
      dateReceived: toIso(x.dateReceived),
      completedAt: toIso(x.completedAt),
      location: location,
      locationPrecise,
      locationAddress: x.locationAddress ?? undefined,
      carpNumber: x.carpNumber ?? undefined,
    } as Customer;
}


export async function getAllCustomers(): Promise<Customer[]> {
    if (!firestore) return [];
    const snap = await getDocs(query(getCustomersCol(), orderBy("name")));
    return snap.docs.map(mapCustomerFromDoc);
}

export async function getCustomerById(id: string): Promise<Customer | null> {
    if (!firestore) return null;
    const docRef = doc(firestore, "customers", id);
    const snap = await getDoc(docRef);
    if (!snap.exists()) {
        return null;
    }
    return mapCustomerFromDoc(snap);
}

export async function getFullCustomerRecord(customerId: string): Promise<FullCustomerRecord | null> {
    if (!firestore) return null;
    const customer = await getCustomerById(customerId);
    if (!customer) return null;

    const appointments = await getAppointmentsForCustomer(customerId);
    const callLogs = await getCallLogsForCustomer(customerId);
    const notes = await getNotesForCustomer(customerId);

    return {
        ...customer,
        appointments,
        callLogs,
        notes,
    }
}


export async function addCustomer(c: Omit<Customer, "id" | "lastUpdated">) {
  if (!firestore) throw new Error("Firestore not initialized");
  const payload: { [key: string]: any } = { ...c };
  if (c.groupId === undefined) {
    delete payload.groupId;
  }
  payload.status = c.status ?? "pending";
  payload.lastUpdated = serverTimestamp();
  if (c.location) {
    payload.location = c.location;
    payload.locationPrecise = c.locationPrecise ?? false;
  } else {
    const geo = await geocodeAddress(c.address);
    payload.location = { lat: geo.lat, lon: geo.lon };
    payload.locationPrecise = geo.precise;
  }
  payload.locationAddress = c.address;
  if (c.dateReceived) {
    payload.dateReceived = Timestamp.fromDate(new Date(c.dateReceived));
  }


  const ref = await addDoc(getCustomersCol(), payload);
  return { id: ref.id, ...c, location: payload.location, locationPrecise: payload.locationPrecise };
}

export async function updateCustomer(id: string, data: Partial<Omit<Customer, 'id'>>) {
  if (!firestore) throw new Error("Firestore not initialized");
  const docRef = doc(firestore, "customers", id);
  const payload: { [key: string]: any } = { ...data, lastUpdated: serverTimestamp() };

  if (data.address && !data.location) {
      const geo = await geocodeAddress(data.address);
      payload.location = { lat: geo.lat, lon: geo.lon };
      payload.locationPrecise = geo.precise;
  } else if (data.location) {
      payload.locationPrecise = data.locationPrecise ?? false;
  }
  // Stamp which address the stored coords belong to, so an address edit can
  // never leave a Maps link pointing at the previous location.
  if (data.address && payload.location) {
      payload.locationAddress = data.address;
  }

  if (Object.prototype.hasOwnProperty.call(data, 'groupId') && data.groupId === undefined) {
      payload.groupId = deleteField();
  }

  if (data.dateReceived) {
    payload.dateReceived = Timestamp.fromDate(new Date(data.dateReceived));
  }
  
  if (data.status === 'complete') {
    payload.completedAt = serverTimestamp();
  }

  await updateDoc(docRef, payload);
}

export async function importCustomersFromCSV(csvText: string): Promise<{ success: boolean, count: number, error?: string }> {
  if (!firestore) {
    return { success: false, count: 0, error: "Database not initialized." };
  }
  
  const lines = csvText.split(/\r\n|\n/).filter(line => line.trim() !== '');
  if (lines.length <= 1) {
    return { success: false, count: 0, error: "CSV file is empty or contains only a header." };
  }

  const header = lines[0].split(',').map(h => h.trim().toLowerCase());
  const nameIndex = header.indexOf('name');
  const phoneIndex = header.indexOf('phone');
  const addressIndex = header.indexOf('address');

  if (nameIndex === -1 || phoneIndex === -1 || addressIndex === -1) {
    return { success: false, count: 0, error: "CSV must contain 'name', 'phone', and 'address' columns." };
  }

  const batch = writeBatch(firestore);
  let importedCount = 0;

  for (let i = 1; i < lines.length; i++) {
    const data = lines[i].split(',');
    const name = data[nameIndex]?.trim();
    const phone = data[phoneIndex]?.trim();
    const address = data[addressIndex]?.trim();

    if (name && phone && address) {
      const customerDocRef = doc(getCustomersCol());
      batch.set(customerDocRef, {
        name,
        phone,
        address,
        status: "pending",
        lastUpdated: serverTimestamp(),
        dateReceived: serverTimestamp(),
        location: estimateCoordinates(address),
      });
      importedCount++;
    }
  }

  if (importedCount === 0) {
    return { success: false, count: 0, error: "No valid customer rows found in the CSV file." };
  }

  try {
    await batch.commit();
    return { success: true, count: importedCount };
  } catch (e) {
    console.error("Batch import failed:", e);
    return { success: false, count: 0, error: "An error occurred while saving the data." };
  }
}


// --- GROUPS ---
export async function addGroup(name: string, members: Customer[]): Promise<string> {
    if (!firestore) throw new Error("Firestore not initialized");
    if (!name || members.length === 0) {
        throw new Error("Group name and at least one member are required.");
    }
    
    const batch = writeBatch(firestore);
    
    const groupRef = doc(getGroupsCol());
    batch.set(groupRef, { 
        name, 
        memberCount: members.length, 
        createdAt: serverTimestamp() 
    });

    members.forEach(member => {
        const customerRef = doc(firestore, "customers", member.id);
        batch.update(customerRef, { groupId: groupRef.id, lastUpdated: serverTimestamp() });
    });

    await batch.commit();
    
    return groupRef.id;
}

export async function getAllGroups(): Promise<CustomerGroup[]> {
    if (!firestore) return [];
    const snapshot = await getDocs(query(getGroupsCol(), orderBy("name")));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CustomerGroup));
}


export async function deleteGroup(groupId: string): Promise<void> {
    if (!firestore) throw new Error("Firestore not initialized");
    const batch = writeBatch(firestore);

    // 1. Delete the group document itself
    const groupRef = doc(firestore, "groups", groupId);
    batch.delete(groupRef);

    // 2. Find all customers in that group
    const q = query(collection(firestore, "customers"), where("groupId", "==", groupId));
    const membersSnap = await getDocs(q);

    // 3. Update each member to remove them from the group
    membersSnap.forEach(memberDoc => {
        const memberRef = doc(firestore, "customers", memberDoc.id);
        batch.update(memberRef, {
            groupId: deleteField(),
            lastUpdated: serverTimestamp()
        });
    });

    await batch.commit();
}


// --- APPOINTMENTS ---
export async function getAllAppointments(): Promise<Appointment[]> {
  if (!firestore) return [];
  const snap = await getDocs(getAppointmentsCol());
  return snap.docs.map(d => {
    const x = d.data() as any;
    return {
      id: d.id,
      ...x,
      date: toIso(x.date)!,
      createdAt: toIso(x.createdAt),
    } as Appointment;
  });
}

export async function getAppointmentsForCustomer(customerId: string): Promise<Appointment[]> {
  if (!firestore) return [];
  const qy = query(getAppointmentsCol(), where("customerId","==",customerId), orderBy("date", "desc"));
  const snap = await getDocs(qy);
  return snap.docs.map(d => {
    const x = d.data() as any;
    return {
      id: d.id,
      ...x,
      date: toIso(x.date)!,
      createdAt: toIso(x.createdAt),
    } as Appointment;
  });
}


export async function addAppointment(a: Omit<Appointment,"id">): Promise<Appointment> {
  if (!firestore) throw new Error("Firestore not initialized");
  const ref = await addDoc(getAppointmentsCol(), {
    ...a,
    date: new Date(a.date as any),
    createdAt: serverTimestamp(),
  });
  const fresh = (await getDoc(ref)).data()!;
  return { id: ref.id, ...(a as any), date: toIso(fresh.date)!, createdAt: toIso(fresh.createdAt) };
}

export async function updateAppointment(id: string, data: Partial<Appointment>) {
  if (!firestore) throw new Error("Firestore not initialized");
  await updateDoc(doc(firestore,"appointments", id), data);
}

export async function deleteAppointment(appointmentId: string): Promise<void> {
    if (!firestore) throw new Error("Firestore not initialized");
    await deleteDoc(doc(firestore, "appointments", appointmentId));
}

export async function scheduleAppointmentAtomic(opts: {
  customer: Customer;
  jobType: "Carpentry" | "Paint" | "Gas" | "Provide Notice";
  scheduledDate: Date;
  startTime: string;
  workDescription: string;
  estimatedTime: string;
  carpenterName?: string;
}) {
  if (!firestore) throw new Error("Firestore not initialized");
  const { customer, jobType, scheduledDate, startTime, workDescription, estimatedTime, carpenterName } = opts;
  const batch = writeBatch(firestore);

  const apptDoc = doc(getAppointmentsCol());

  const appt: Omit<Appointment, "id"> = {
    customerId: customer.id,
    customerName: customer.name,
    jobType: jobType,
    status: "Scheduled",
    date: scheduledDate.toISOString(),
    workDescription,
    estimatedTime,
    ...(carpenterName ? { carpenterName } : {}),
  };
  
  // "Provide Notice" is a quick administrative visit, not a repair job —
  // scheduling it must not disturb whatever status the customer is already in.
  const newStatus: CustomerStatus | null =
    jobType === "Carpentry" ? "scheduled-carpentry" :
    jobType === "Paint"     ? "scheduled-paint"     :
    jobType === "Gas"       ? "scheduled-gas"       : null;

  batch.set(apptDoc, { ...appt, date: scheduledDate, createdAt: serverTimestamp() });
  batch.update(doc(getCustomersCol(), customer.id), {
    ...(newStatus ? { status: newStatus } : {}),
    lastUpdated: serverTimestamp(),
  });

  await batch.commit();
  return { appt: { ...appt, id: apptDoc.id, date: scheduledDate.toISOString() }, newStatus: newStatus ?? customer.status };
}

export async function cancelAppointment(appointmentId: string, customerId: string): Promise<void> {
    if (!firestore) throw new Error("Firestore not initialized");
    const batch = writeBatch(firestore);

    const appointmentRef = doc(getAppointmentsCol(), appointmentId);
    batch.update(appointmentRef, { status: "Canceled" });

    const customerRef = doc(getCustomersCol(), customerId);
    batch.update(customerRef, { status: "reschedule", lastUpdated: serverTimestamp() });

    await batch.commit();
}

export async function reinstateAppointment(
    appointmentId: string,
    customerUpdate?: { customerId: string; status: CustomerStatus }
): Promise<void> {
    if (!firestore) throw new Error("Firestore not initialized");
    const batch = writeBatch(firestore);
    batch.update(doc(firestore, "appointments", appointmentId), { status: "Scheduled" });
    if (customerUpdate) {
        batch.update(doc(firestore, "customers", customerUpdate.customerId), {
            status: customerUpdate.status,
            lastUpdated: serverTimestamp(),
        });
    }
    await batch.commit();
}


// --- CALL LOGS ---
export async function getCallLogsForCustomer(customerId: string): Promise<CallLog[]> {
    if (!firestore) return [];
    const callLogsCol = collection(firestore, 'customers', customerId, 'callLogs');
    const q = query(callLogsCol, orderBy("timestamp", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), timestamp: toIso(doc.data().timestamp)! } as CallLog));
}

export async function addCallLog(data: Omit<CallLog, "id" | "timestamp"> & { timestamp: Date }): Promise<CallLog> {
    if (!firestore) throw new Error("Firestore not initialized");
    const callLogsCol = collection(firestore, 'customers', data.customerId, 'callLogs');
    const payload = {
        ...data,
        timestamp: Timestamp.fromDate(data.timestamp),
    };
    const ref = await addDoc(callLogsCol, payload);
    const freshDoc = await getDoc(ref);
    const freshData = freshDoc.data()!;

    // Auto-status transitions based on total call count.
    // 6+ calls while call-to-schedule-carpentry → not-responsive
    // 6+ calls while call-to-schedule-paint     → submit
    const [allLogsSnap, customerSnap] = await Promise.all([
        getDocs(callLogsCol),
        getDoc(doc(getCustomersCol(), data.customerId)),
    ]);
    const callCount = allLogsSnap.size;
    const currentStatus = (customerSnap.data() as Customer | undefined)?.status;
    let autoStatus: CustomerStatus | null = null;
    if (callCount >= 6) {
        if (currentStatus === 'call-to-schedule-carpentry') autoStatus = 'not-responsive';
        else if (currentStatus === 'call-to-schedule-paint') autoStatus = 'submit';
    }
    if (autoStatus) {
        await updateDoc(doc(getCustomersCol(), data.customerId), {
            status: autoStatus,
            lastUpdated: serverTimestamp(),
        });
    }

    return {
        id: ref.id,
        customerId: data.customerId,
        timestamp: toIso(freshData.timestamp)!,
        notes: data.notes,
    };
}

export async function deleteCallLog(customerId: string, logId: string): Promise<void> {
  if (!firestore) throw new Error("Firestore not initialized");
  await deleteDoc(doc(firestore, "customers", customerId, "callLogs", logId));
}

// --- CALLER NAMES ---
export async function getCallerNames(): Promise<CallerName[]> {
    if (!firestore) return [];
    const snapshot = await getDocs(query(getCallerNamesCol(), orderBy("name")));
    return snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name } as CallerName));
}

export async function addCallerName(name: string): Promise<string> {
    if (!firestore) throw new Error("Firestore not initialized");
    const ref = await addDoc(getCallerNamesCol(), { name });
    return ref.id;
}

export async function deleteCallerName(id: string): Promise<void> {
    if (!firestore) throw new Error("Firestore not initialized");
    await deleteDoc(doc(firestore, "callerNames", id));
}

// --- CARPENTERS ---
export async function getCarpenters(): Promise<Carpenter[]> {
    if (!firestore) return [];
    const snapshot = await getDocs(query(getCarpentersCol(), orderBy("name")));
    return snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name } as Carpenter));
}

export async function addCarpenter(name: string): Promise<string> {
    if (!firestore) throw new Error("Firestore not initialized");
    const ref = await addDoc(getCarpentersCol(), { name });
    return ref.id;
}

export async function deleteCarpenter(id: string): Promise<void> {
    if (!firestore) throw new Error("Firestore not initialized");
    await deleteDoc(doc(firestore, "carpenters", id));
}


// --- NOTES ---
export async function getNotesForCustomer(customerId: string): Promise<Note[]> {
    if (!firestore) return [];
    const notesCol = collection(firestore, 'customers', customerId, 'notes');
    const q = query(notesCol, orderBy("timestamp", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), timestamp: toIso(doc.data().timestamp)! } as Note));
}

export async function addNote(data: Omit<Note, "id" | "timestamp"> & { timestamp: Date }): Promise<Note> {
    if (!firestore) throw new Error("Firestore not initialized");
    const notesCol = collection(firestore, 'customers', data.customerId, 'notes');
    const payload = {
        ...data,
        timestamp: Timestamp.fromDate(data.timestamp),
    };
    const ref = await addDoc(notesCol, payload);
    const freshDoc = await getDoc(ref);
    const freshData = freshDoc.data()!;
    return {
        id: ref.id,
        customerId: data.customerId,
        timestamp: toIso(freshData.timestamp)!,
        text: data.text,
        author: data.author,
    };
}

export async function deleteNote(customerId: string, noteId: string): Promise<void> {
    if (!firestore) throw new Error("Firestore not initialized");
    await deleteDoc(doc(firestore, "customers", customerId, "notes", noteId));
}

// --- NOTE AUTHORS ---
export async function getNoteAuthors(): Promise<NoteAuthor[]> {
    if (!firestore) return [];
    const snapshot = await getDocs(query(getNoteAuthorsCol(), orderBy("name")));
    return snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name } as NoteAuthor));
}

export async function addNoteAuthor(name: string): Promise<string> {
    if (!firestore) throw new Error("Firestore not initialized");
    const ref = await addDoc(getNoteAuthorsCol(), { name });
    return ref.id;
}

export async function deleteNoteAuthor(id: string): Promise<void> {
    if (!firestore) throw new Error("Firestore not initialized");
    await deleteDoc(doc(firestore, "noteAuthors", id));
}

// --- PHOTOS ---

// In-memory cache: customerId → { data, fetchedAt }
const _photoCache = new Map<string, { data: Record<string, Photo[]>; fetchedAt: number }>();
// Per-folder cache: "customerId:folder" → { data, fetchedAt }
const _photoFolderCache = new Map<string, { data: Photo[]; fetchedAt: number }>();
const PHOTO_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Mobile connections drop requests transiently. Retry a couple times with
// backoff before treating a single photo's URL fetch as a real failure.
async function getDownloadURLWithRetry(itemRef: Parameters<typeof getDownloadURL>[0], attempts = 3): Promise<string> {
  for (let i = 0; i < attempts; i++) {
    try {
      return await getDownloadURL(itemRef);
    } catch (error) {
      if (i === attempts - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, 300 * Math.pow(3, i)));
    }
  }
  throw new Error('unreachable');
}

export function invalidatePhotoCache(customerId: string) {
  _photoCache.delete(customerId);
  for (const key of _photoFolderCache.keys()) {
    if (key.startsWith(`${customerId}:`)) _photoFolderCache.delete(key);
  }
}

// Sort photos by user-specified order if present, otherwise by name (upload order).
export function applyPhotoOrder(photos: Photo[], order?: string[]): Photo[] {
  if (!order || order.length === 0) {
    return [...photos].sort((a, b) => a.name.localeCompare(b.name));
  }
  const orderIndex = new Map(order.map((name, i) => [name, i]));
  return [...photos].sort((a, b) => {
    const ai = orderIndex.has(a.name) ? orderIndex.get(a.name)! : Number.MAX_SAFE_INTEGER;
    const bi = orderIndex.has(b.name) ? orderIndex.get(b.name)! : Number.MAX_SAFE_INTEGER;
    if (ai !== bi) return ai - bi;
    // Items not in the saved order fall to the end and sort by name
    return a.name.localeCompare(b.name);
  });
}

export async function getCustomerPhotos(customerId: string): Promise<Record<string, Photo[]>> {
  if (!storage || !firestore) return { before: [], 'after-carpentry': [], 'after-paint': [], receipts: [] };

  // Return cached result if still fresh
  const cached = _photoCache.get(customerId);
  if (cached && Date.now() - cached.fetchedAt < PHOTO_CACHE_TTL_MS) {
    return cached.data;
  }

  const folders = ['before', 'after-carpentry', 'after-paint', 'receipts'] as const;

  const fetchPhotosFromFolder = async (folder: 'before' | 'after-carpentry' | 'after-paint' | 'receipts'): Promise<Photo[]> => {
      const folderRef = storageRef(storage, `customers/${customerId}/photos/${folder}`);
      try {
          const res = await listAll(folderRef);
          const results = await Promise.allSettled(res.items.map(async (itemRef): Promise<Photo> => {
              const url = await getDownloadURLWithRetry(itemRef);
              return { name: itemRef.name, url, folder };
          }));
          // A single flaky getDownloadURL (common on mobile networks) must not
          // wipe out every other photo that resolved fine in this folder.
          results.filter(r => r.status === 'rejected').forEach(r =>
              console.error(`Error fetching a photo URL in ${folder}:`, (r as PromiseRejectedResult).reason)
          );
          return results
              .filter((r): r is PromiseFulfilledResult<Photo> => r.status === 'fulfilled')
              .map(r => r.value);
      } catch (error) {
          if (error instanceof Error && (error as any).code === 'storage/object-not-found') {
              return [];
          }
          console.error(`Error listing photos in ${folder}:`, error);
          return [];
      }
  };

  // Fetch photos and customer doc (for photoOrder) in parallel
  const [photoResults, customerSnap] = await Promise.all([
    Promise.all(folders.map(f => fetchPhotosFromFolder(f))),
    getDoc(doc(firestore, "customers", customerId)),
  ]);

  const photoOrder = (customerSnap.data() as Customer | undefined)?.photoOrder;

  const data = {
    before: applyPhotoOrder(photoResults[0], photoOrder?.before),
    'after-carpentry': applyPhotoOrder(photoResults[1], photoOrder?.['after-carpentry']),
    'after-paint': applyPhotoOrder(photoResults[2], photoOrder?.['after-paint']),
    receipts: applyPhotoOrder(photoResults[3], photoOrder?.receipts),
  };

  _photoCache.set(customerId, { data, fetchedAt: Date.now() });
  return data;
}

// Fetch photos for a single folder. Used by the calendar view for progressive loading.
// Results are cached separately per-folder so each folder can be warmed independently.
export async function getCustomerPhotosByFolder(
  customerId: string,
  folder: 'before' | 'after-carpentry' | 'after-paint' | 'receipts'
): Promise<Photo[]> {
  if (!storage) return [];

  const cacheKey = `${customerId}:${folder}`;
  const cached = _photoFolderCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < PHOTO_CACHE_TTL_MS) {
    return cached.data;
  }

  const folderRef = storageRef(storage, `customers/${customerId}/photos/${folder}`);
  try {
    const res = await listAll(folderRef);
    const results = await Promise.allSettled(
      res.items.map(async (itemRef) => {
        const url = await getDownloadURLWithRetry(itemRef);
        return { name: itemRef.name, url, folder } as Photo;
      })
    );
    results.filter(r => r.status === 'rejected').forEach(r =>
      console.error(`Error fetching a photo URL in ${folder}:`, (r as PromiseRejectedResult).reason)
    );
    const photos = results
      .filter((r): r is PromiseFulfilledResult<Photo> => r.status === 'fulfilled')
      .map(r => r.value);
    _photoFolderCache.set(cacheKey, { data: photos, fetchedAt: Date.now() });
    return photos;
  } catch (error) {
    if (error instanceof Error && (error as any).code === 'storage/object-not-found') return [];
    console.error(`Error listing photos in ${folder}:`, error);
    return [];
  }
}

// Persist a custom photo order for a folder. Pass the array of photo file names
// in the desired order. Invalidates the photo cache so the new order is read on next load.
export async function updatePhotoOrder(
  customerId: string,
  folder: 'before' | 'after-carpentry' | 'after-paint' | 'receipts',
  orderedNames: string[]
): Promise<void> {
  if (!firestore) return;
  await updateDoc(doc(firestore, "customers", customerId), {
    [`photoOrder.${folder}`]: orderedNames,
  });
  invalidatePhotoCache(customerId);
}

export async function updateCustomerWriteup(
  customerId: string,
  writeup: string
): Promise<void> {
  if (!firestore) throw new Error('Firestore not initialized');
  await updateDoc(doc(firestore, "customers", customerId), {
    writeup,
    writeupUpdatedAt: new Date().toISOString(),
  });
}

// ----------------- Write-up AI feedback (global memory) -----------------
// Crew submits corrections / preferences after generating a write-up.
// All entries are sent to the AI on subsequent generations as a "user
// corrections" block so it learns shared house style across customers.
export interface WriteupFeedback {
  id: string;
  text: string;
  customerId?: string;
  customerName?: string;
  author?: string;
  timestamp: string; // ISO
}

export async function addWriteupFeedback(data: {
  text: string;
  customerId?: string;
  customerName?: string;
  author?: string;
}): Promise<string> {
  if (!firestore) throw new Error('Firestore not initialized');
  const cleaned = data.text.trim();
  if (!cleaned) throw new Error('Feedback text is empty');
  const ref = await addDoc(collection(firestore, 'writeup_feedback'), {
    text: cleaned,
    customerId: data.customerId ?? null,
    customerName: data.customerName ?? null,
    author: data.author ?? null,
    timestamp: new Date().toISOString(),
  });
  return ref.id;
}

export async function getRecentWriteupFeedback(limit: number = 50): Promise<WriteupFeedback[]> {
  if (!firestore) throw new Error('Firestore not initialized');
  // Newest first; cap at `limit` to keep the prompt token cost bounded.
  const q = query(collection(firestore, 'writeup_feedback'), orderBy('timestamp', 'desc'));
  const snap = await getDocs(q);
  const all = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as WriteupFeedback[];
  return all.slice(0, limit);
}

export async function setBilledStatus(customerId: string, billed: boolean): Promise<void> {
  if (!firestore) throw new Error('Firestore not initialized');
  await updateDoc(doc(getCustomersCol(), customerId), {
    billed,
    billedAt: billed ? serverTimestamp() : null,
    lastUpdated: serverTimestamp(),
  });
}

export async function deleteWriteupFeedback(id: string): Promise<void> {
  if (!firestore) throw new Error('Firestore not initialized');
  await deleteDoc(doc(firestore, 'writeup_feedback', id));
}

// Rule 1: carpentry appt date passes (status=scheduled-carpentry) → call-to-schedule-paint
// Rule 4: paint appt date passes (status=scheduled-paint) → submit
export async function applyDateBasedStatusTransitions(
  customer: Customer,
  appointments: Appointment[]
): Promise<CustomerStatus | null> {
  if (!firestore) return null;
  const now = new Date();
  let newStatus: CustomerStatus | null = null;

  if (customer.status === 'scheduled-carpentry') {
    const done = appointments.find(
      (a) => a.jobType === 'Carpentry' && a.status === 'Scheduled' && new Date(a.date) < now
    );
    if (done) newStatus = 'call-to-schedule-paint';
  } else if (customer.status === 'scheduled-paint') {
    const done = appointments.find(
      (a) => a.jobType === 'Paint' && a.status === 'Scheduled' && new Date(a.date) < now
    );
    if (done) newStatus = 'submit';
  }

  if (newStatus) {
    await updateDoc(doc(getCustomersCol(), customer.id), {
      status: newStatus,
      lastUpdated: serverTimestamp(),
    });
  }
  return newStatus;
}

/* ------------------------------------------------------------------ */
/* Removing a resident                                                 */
/* ------------------------------------------------------------------ */

/**
 * Everything that would be destroyed along with a resident.
 *
 * Shown to the user BEFORE they confirm, because "delete this resident" reads
 * like one row disappearing and is in fact a job history, a call log, a set of
 * notes and a folder of site photos. Nobody can consent to losing something
 * they were not told about.
 */
export interface DeletionImpact {
  appointments: number;
  notes: number;
  callLogs: number;
  photos: number;
}

const PHOTO_FOLDERS = ['before', 'after-carpentry', 'after-paint', 'receipts'] as const;

/** Every stored photo path for a resident, across all four folders. */
async function listAllPhotoRefs(customerId: string) {
  if (!storage) return [];
  const found = await Promise.all(
    PHOTO_FOLDERS.map(async folder => {
      try {
        const res = await listAll(storageRef(storage!, `customers/${customerId}/photos/${folder}`));
        return res.items;
      } catch {
        // A folder that was never used does not exist in Storage. Not an error.
        return [];
      }
    })
  );
  return found.flat();
}

/**
 * Count what a deletion would take with it, without changing anything.
 *
 * Read-only, and safe to call while the confirmation dialog is open.
 */
export async function getDeletionImpact(customerId: string): Promise<DeletionImpact> {
  const [appointments, notes, callLogs, photos] = await Promise.all([
    getAppointmentsForCustomer(customerId),
    getNotesForCustomer(customerId),
    getCallLogsForCustomer(customerId),
    listAllPhotoRefs(customerId),
  ]);
  return {
    appointments: appointments.length,
    notes: notes.length,
    callLogs: callLogs.length,
    photos: photos.length,
  };
}

/**
 * Delete a resident and everything filed under them.
 *
 * The resident document is removed LAST, on purpose. If this fails halfway —
 * a dropped connection, a Storage permission — the resident is still in the
 * list, visibly, and the operation can be retried. Deleting the document first
 * would leave appointments pointing at a resident who no longer exists and
 * photos nobody can reach, with nothing on screen to say so.
 *
 * Photo deletions are allowed to fail individually. A file already gone, or one
 * Storage refuses, must not strand the record in a half-deleted state forever;
 * the count of what actually went is returned so the caller can be honest about
 * it rather than claiming a clean sweep.
 */
export async function deleteCustomerCompletely(customerId: string): Promise<DeletionImpact> {
  if (!firestore) throw new Error('Database not initialized.');

  const removed: DeletionImpact = { appointments: 0, notes: 0, callLogs: 0, photos: 0 };

  // 1. Storage first — the only part not covered by Firestore's batching, and
  //    the part most likely to be refused.
  const photoRefs = await listAllPhotoRefs(customerId);
  const photoResults = await Promise.allSettled(photoRefs.map(r => deleteObject(r)));
  removed.photos = photoResults.filter(r => r.status === 'fulfilled').length;
  photoResults
    .filter(r => r.status === 'rejected')
    .forEach(r => console.error('Failed to delete a photo:', (r as PromiseRejectedResult).reason));

  // 2. Group membership. Membership lives on the group document now, so a
  //    deleted resident would otherwise leave their id behind in every group
  //    that held them, pointing at a record that no longer exists. This can
  //    dissolve a group that drops below two members.
  try {
    await removeCustomerFromAllGroups(customerId);
  } catch (e) {
    console.error('Could not remove the resident from their groups:', e);
  }

  // 3. Subcollections and appointments, in one batch so they cannot half-apply.
  const batch = writeBatch(firestore);

  const appts = await getDocs(query(getAppointmentsCol(), where('customerId', '==', customerId)));
  appts.forEach(d => { batch.delete(d.ref); removed.appointments++; });

  const notes = await getDocs(collection(firestore, 'customers', customerId, 'notes'));
  notes.forEach(d => { batch.delete(d.ref); removed.notes++; });

  const logs = await getDocs(collection(firestore, 'customers', customerId, 'callLogs'));
  logs.forEach(d => { batch.delete(d.ref); removed.callLogs++; });

  // 4. The resident itself, in the same batch and written last.
  batch.delete(doc(firestore, 'customers', customerId));

  await batch.commit();
  invalidatePhotoCache(customerId);
  return removed;
}

/* ------------------------------------------------------------------ */
/* Group membership (many-to-many)                                     */
/* ------------------------------------------------------------------ */

/**
 * A group needs at least this many residents to exist.
 *
 * A group of one is not a group — it is a resident with a label nobody asked
 * for. When membership falls below this, the group document is deleted and the
 * last member is simply left ungrouped.
 */
export const MIN_GROUP_SIZE = 2;

/** Members of a group, tolerating documents written before memberIds existed. */
export const groupMemberIds = (g: Pick<CustomerGroup, 'memberIds'>): string[] =>
  Array.isArray(g.memberIds) ? g.memberIds : [];

/**
 * Every group a resident belongs to.
 *
 * Reads from the group side, which is the source of truth. `Customer.groupId`
 * is still written for now, but it can only ever hold one of these.
 */
export function groupsForCustomer(customerId: string, groups: CustomerGroup[]): CustomerGroup[] {
  return groups.filter(g => groupMemberIds(g).includes(customerId));
}

/**
 * Delete a group that has fallen below the minimum, and release its last member.
 *
 * Called after every removal. Returns true when the group was dissolved, so
 * callers can say so rather than leaving the user to notice it vanished.
 */
async function dissolveIfTooSmall(groupId: string): Promise<boolean> {
  if (!firestore) return false;
  const groupRef = doc(firestore, 'groups', groupId);
  const snap = await getDoc(groupRef);
  if (!snap.exists()) return false;

  const remaining: string[] = Array.isArray(snap.data()?.memberIds) ? snap.data()!.memberIds : [];
  if (remaining.length >= MIN_GROUP_SIZE) return false;

  const batch = writeBatch(firestore);
  batch.delete(groupRef);
  // The stragglers keep their record; they just stop being in a group.
  for (const id of remaining) {
    batch.update(doc(firestore, 'customers', id), {
      groupId: deleteField(),
      lastUpdated: serverTimestamp(),
    });
  }
  await batch.commit();
  return true;
}

/**
 * Make sure a group's memberIds exists before we union or remove against it.
 *
 * Groups created before membership moved onto the group document have no
 * memberIds at all — their members are only discoverable by scanning customers
 * for a matching groupId. An arrayUnion against that missing field would
 * CREATE it holding just the people being added, and since readers prefer
 * memberIds once it exists, everybody already in the group would silently fall
 * out of it.
 *
 * So each group is backfilled the first time it is edited. That makes the
 * migration lazy and self-healing rather than something that must be run
 * before the feature is safe.
 */
async function seedMemberIdsIfMissing(groupId: string): Promise<void> {
  if (!firestore) return;
  const groupRef = doc(firestore, 'groups', groupId);
  const snap = await getDoc(groupRef);
  if (!snap.exists() || Array.isArray(snap.data()?.memberIds)) return;

  const existing = await getDocs(query(getCustomersCol(), where('groupId', '==', groupId)));
  const ids = existing.docs.map(d => d.id);
  await updateDoc(groupRef, { memberIds: ids, memberCount: ids.length });
}

/**
 * Add residents to a group that already exists.
 *
 * One write to the group document however many people are being added, and
 * arrayUnion means adding somebody who is already in it is a no-op rather than
 * a duplicate.
 *
 * `groupId` on each customer is still written (dual-write) so that every
 * existing reader keeps working untouched. It holds only one group, so it
 * records the most recent one; memberIds is what actually decides membership.
 */
export async function addToGroup(groupId: string, customerIds: string[]): Promise<void> {
  if (!firestore) throw new Error('Firestore not initialized');
  if (!customerIds.length) return;
  await seedMemberIdsIfMissing(groupId);

  const groupRef = doc(firestore, 'groups', groupId);
  const batch = writeBatch(firestore);
  batch.update(groupRef, { memberIds: arrayUnion(...customerIds) });
  for (const id of customerIds) {
    batch.update(doc(firestore, 'customers', id), { groupId, lastUpdated: serverTimestamp() });
  }
  await batch.commit();

  // memberCount is derived, never authored — recomputed from what was written.
  const after = await getDoc(groupRef);
  const ids: string[] = Array.isArray(after.data()?.memberIds) ? after.data()!.memberIds : [];
  await updateDoc(groupRef, { memberCount: ids.length });
}

/**
 * Take residents out of a group.
 *
 * If that leaves fewer than MIN_GROUP_SIZE behind, the group dissolves and the
 * remaining member is released — reported back so the caller can say so.
 */
export async function removeFromGroup(
  groupId: string,
  customerIds: string[]
): Promise<{ dissolved: boolean }> {
  if (!firestore) throw new Error('Firestore not initialized');
  if (!customerIds.length) return { dissolved: false };
  await seedMemberIdsIfMissing(groupId);

  const groupRef = doc(firestore, 'groups', groupId);
  const batch = writeBatch(firestore);
  batch.update(groupRef, { memberIds: arrayRemove(...customerIds) });
  for (const id of customerIds) {
    // Only clear groupId if it points at THIS group; the resident may be in others.
    batch.update(doc(firestore, 'customers', id), {
      groupId: deleteField(),
      lastUpdated: serverTimestamp(),
    });
  }
  await batch.commit();

  const dissolved = await dissolveIfTooSmall(groupId);
  if (!dissolved) {
    const after = await getDoc(groupRef);
    const ids: string[] = Array.isArray(after.data()?.memberIds) ? after.data()!.memberIds : [];
    await updateDoc(groupRef, { memberCount: ids.length });
  }
  return { dissolved };
}

/**
 * Take a resident out of every group they are in.
 *
 * Needed when a resident is deleted: membership lives on the group now, so a
 * deleted resident would otherwise leave their id behind in every group that
 * held them, pointing at a record that no longer exists.
 */
export async function removeCustomerFromAllGroups(customerId: string): Promise<void> {
  if (!firestore) return;
  const snap = await getDocs(query(getGroupsCol(), where('memberIds', 'array-contains', customerId)));
  for (const g of snap.docs) {
    await updateDoc(g.ref, { memberIds: arrayRemove(customerId) });
    const dissolved = await dissolveIfTooSmall(g.id);
    if (!dissolved) {
      const after = await getDoc(g.ref);
      const ids: string[] = Array.isArray(after.data()?.memberIds) ? after.data()!.memberIds : [];
      await updateDoc(g.ref, { memberCount: ids.length });
    }
  }
}

export interface GroupMigrationPlan {
  groups: { id: string; name: string; alreadyHasMemberIds: boolean; wouldSet: string[] }[];
  ungroupedResidents: number;
}

/**
 * Work out what backfilling memberIds from the old single groupId would do.
 *
 * Read-only. Runs first so the change can be read before it is made — 20
 * residents' membership is not something to rewrite on trust.
 */
export async function planGroupMembershipMigration(): Promise<GroupMigrationPlan> {
  if (!firestore) return { groups: [], ungroupedResidents: 0 };
  const [groupsSnap, customersSnap] = await Promise.all([
    getDocs(getGroupsCol()),
    getDocs(getCustomersCol()),
  ]);

  const byGroup = new Map<string, string[]>();
  let ungrouped = 0;
  customersSnap.forEach(d => {
    const gid = d.data()?.groupId;
    if (typeof gid === 'string' && gid) {
      byGroup.set(gid, [...(byGroup.get(gid) ?? []), d.id]);
    } else {
      ungrouped++;
    }
  });

  return {
    groups: groupsSnap.docs.map(g => ({
      id: g.id,
      name: g.data()?.name ?? '(unnamed)',
      alreadyHasMemberIds: Array.isArray(g.data()?.memberIds),
      wouldSet: byGroup.get(g.id) ?? [],
    })),
    ungroupedResidents: ungrouped,
  };
}

/**
 * Backfill memberIds from the old groupId field.
 *
 * `groupId` is deliberately left in place. Readers move to memberIds, and if
 * anything is wrong the fix is to revert the code — the original membership is
 * still on every customer record, untouched.
 */
export async function migrateGroupMembership(): Promise<GroupMigrationPlan> {
  const plan = await planGroupMembershipMigration();
  if (!firestore) return plan;

  const batch = writeBatch(firestore);
  for (const g of plan.groups) {
    batch.update(doc(firestore, 'groups', g.id), {
      memberIds: g.wouldSet,
      memberCount: g.wouldSet.length,
    });
  }
  await batch.commit();
  return plan;
}
